import React, {useEffect, useMemo, useState} from 'react';
import {Badge, Button, Container, Form, Table} from 'react-bootstrap';

import styles from '../Wizard.module.scss';

const STATUS_LABELS = {
    valid: "Valid",
    consumed: "Consumed",
    expired: "Expired",
    transferred: "Transferred / different wallet",
};

const formatDate = value => {
    if (!value) return null;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const tokenDetail = token => {
    if (token.status === "consumed") {
        const consumedBy = token?.consumed_by;
        const sourceLabel = consumedBy?.label
            || (consumedBy?.event_id ? `event #${consumedBy.event_id}` : null)
            || (consumedBy?.transaction_id ? `transaction #${consumedBy.transaction_id}` : null)
            || (consumedBy?.order_id ? `order #${consumedBy.order_id}` : null)
            || consumedBy?.type;
        const source = sourceLabel ? ` by ${sourceLabel}` : "";
        const consumedAt = formatDate(token.consumed_at);
        return `Consumed${source}${consumedAt ? ` on ${consumedAt}` : ""}`;
    }

    if (token.status === "expired") {
        const expiresAt = formatDate(token.expires_at);
        return expiresAt ? `Expired on ${expiresAt}` : "Expired";
    }

    if (token.status === "transferred") return "Transferred / different wallet";

    const expiresAt = formatDate(token.expires_at);
    return expiresAt ? `Available until ${expiresAt}` : "Available in the patron's wallet";
};

export const TokenHandling = (props) => {
    const {stepUpdate} = props;
    const tokens = useMemo(() => props?.tokenOptions?.tokens || [], [props?.tokenOptions?.tokens]);
    const selectableIds = useMemo(() => tokens
        .filter(token => token.status === "valid" && token.can_claw_back)
        .map(token => +token.id), [tokens]);
    const selectableIdSet = useMemo(() => new Set(selectableIds), [selectableIds]);
    const [selectedIds, setSelectedIds] = useState(() => (props?.stepData?.token_handling?.token_ids || [])
        .map(id => +id)
        .filter(id => selectableIdSet.has(id)));

    useEffect(() => {
        setSelectedIds(previous => previous.filter(id => selectableIdSet.has(+id)));
    }, [selectableIdSet]);

    useEffect(() => {
        stepUpdate(props.stepId, {
            token_handling: {
                confirmed: true,
                token_ids: selectedIds,
            }
        });
    }, [stepUpdate, props.stepId, selectedIds]);

    const toggleToken = tokenId => {
        setSelectedIds(previous => previous.includes(tokenId)
            ? previous.filter(id => id !== tokenId)
            : [...previous, tokenId]);
    };

    return (
        <Container fluid data-cy="refund-token-handling">
            <h4 className={styles.title}>How should the purchased tokens be handled?</h4>
            <span className={styles.subtitle}>
                Select any currently valid tokens to claw back. Unselected tokens remain in the patron's wallet.
            </span>
            <hr/>
            <div className="d-flex flex-wrap align-items-center mb-3">
                <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    className="me-2 mb-2"
                    disabled={selectableIds.length === 0 || selectedIds.length === selectableIds.length}
                    onClick={() => setSelectedIds(selectableIds)}
                    data-cy="refund-token-select-all"
                >
                    Select all valid
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    className="me-3 mb-2"
                    disabled={selectedIds.length === 0}
                    onClick={() => setSelectedIds([])}
                    data-cy="refund-token-select-none"
                >
                    Select none
                </Button>
                <span className="small mb-2" data-cy="refund-token-selection-count">
                    {selectedIds.length} of {selectableIds.length} valid token{selectableIds.length === 1 ? "" : "s"} selected for clawback
                </span>
            </div>
            <Table responsive hover className="mt-0">
                <thead>
                    <tr>
                        <th scope="col">Claw back</th>
                        <th scope="col">Token</th>
                        <th scope="col">Product</th>
                        <th scope="col">Status</th>
                        <th scope="col">Details</th>
                    </tr>
                </thead>
                <tbody>
                    {tokens.map(token => {
                        const tokenId = +token.id;
                        const selectable = selectableIdSet.has(tokenId);

                        return (
                            <tr key={`refund-token-${token.id}`} className={selectable ? "" : "text-muted"}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        id={`refund-token-${token.id}`}
                                        checked={selectedIds.includes(tokenId)}
                                        disabled={!selectable}
                                        onChange={() => toggleToken(tokenId)}
                                        aria-label={`Claw back token ${token.id}`}
                                        data-cy={`refund-token-checkbox-${token.id}`}
                                    />
                                </td>
                                <td>#{token.id}</td>
                                <td>
                                    <span>{token.product_name || `Product #${token.product_id}`}</span>
                                    {token.product_variant_name && <><br/><span className="small">{token.product_variant_name}</span></>}
                                </td>
                                <td>
                                    {STATUS_LABELS[token.status] || token.status}
                                </td>
                                <td>{tokenDetail(token)}</td>
                            </tr>
                        );
                    })}
                    {tokens.length === 0 &&
                        <tr>
                            <td colSpan={5}>No purchase-linked token records were found.</td>
                        </tr>
                    }
                </tbody>
            </Table>
            <p className="small mb-0">
                Clawed-back tokens remain in wallet history, but will be expired immediately and can no longer be redeemed.
            </p>
        </Container>
    );
};
